import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const ENVELOPE_CURVES = Object.freeze(['linear', 'exponential', 's-curve'] as const);
export type EnvelopeCurve = (typeof ENVELOPE_CURVES)[number];

export const ENVELOPE_MODES = Object.freeze(['gate', 'one-shot', 'loop'] as const);
export type EnvelopeMode = (typeof ENVELOPE_MODES)[number];
export type EnvelopeStage = 'idle' | 'attack' | 'decay' | 'stage' | 'sustain' | 'release';

export interface EnvelopeSegment {
  readonly durationSeconds: number;
  readonly targetLevel: number;
  readonly curve?: EnvelopeCurve;
}

export interface EnvelopeOptions {
  readonly sampleRate: number;
  readonly attackSeconds?: number;
  readonly decaySeconds?: number;
  readonly sustainLevel?: number;
  readonly releaseSeconds?: number;
  readonly mode?: EnvelopeMode;
  /** Optional arbitrary stage program. Omitting it retains the ADSR API and behavior. */
  readonly stages?: readonly EnvelopeSegment[];
}

interface ValidatedSegment {
  readonly durationSeconds: number;
  readonly targetLevel: number;
  readonly curve: EnvelopeCurve;
  readonly legacyStage: 'attack' | 'decay' | 'stage';
}

const EXPONENTIAL_DENOMINATOR = Math.expm1(4);

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
  }
};

const validateCurve = (curve: EnvelopeCurve | undefined): EnvelopeCurve => {
  const resolved = curve ?? 'linear';
  if (!(ENVELOPE_CURVES as readonly string[]).includes(resolved)) {
    throw new RangeError('envelope curve is invalid');
  }
  return resolved;
};

const curveValue = (curve: EnvelopeCurve, progress: number): number => {
  if (curve === 'linear') return progress;
  if (curve === 's-curve') return progress * progress * (3 - 2 * progress);
  return Math.expm1(4 * progress) / EXPONENTIAL_DENOMINATOR;
};

/**
 * Host-independent, allocation-free-during-render envelope core. Stage timing
 * and edge state survive arbitrary block segmentation. Levels are always
 * finite and clamped to the control-signal interval [0, 1].
 */
export class EnvelopeGenerator {
  readonly sampleRate: number;
  stage: EnvelopeStage = 'idle';

  private attackSeconds: number;
  private decaySeconds: number;
  private sustainLevel: number;
  private releaseSeconds: number;
  private mode: EnvelopeMode;
  private usesLegacyAdsr: boolean;
  private stages: readonly ValidatedSegment[];
  private hasTimedStages: boolean;

  private value = 0;
  private gateHigh = false;
  private stageIndex = 0;
  private stageStartValue = 0;
  private stageTarget = 0;
  private stageCurve: EnvelopeCurve = 'linear';
  private stageSamples = 0;
  private stageElapsed = 0;

  constructor(options: EnvelopeOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.attackSeconds = options.attackSeconds ?? 0.01;
    this.decaySeconds = options.decaySeconds ?? 0.1;
    this.sustainLevel = options.sustainLevel ?? 0.7;
    this.releaseSeconds = options.releaseSeconds ?? 0.2;
    this.mode = options.mode ?? 'gate';
    this.usesLegacyAdsr = options.stages === undefined;
    this.validateParameters();
    this.stages = options.stages === undefined
      ? this.createAdsrStages()
      : this.validateStages(options.stages);
    this.hasTimedStages = this.stages.some((segment) => this.samples(segment.durationSeconds) > 0);
  }

  reset(): void {
    this.stage = 'idle';
    this.value = 0;
    this.gateHigh = false;
    this.stageIndex = 0;
    this.stageStartValue = 0;
    this.stageTarget = 0;
    this.stageCurve = 'linear';
    this.stageSamples = 0;
    this.stageElapsed = 0;
  }

  /** Replace the arbitrary stage program used on the next trigger. */
  setStages(stages: readonly EnvelopeSegment[]): void {
    this.stages = this.validateStages(stages);
    this.usesLegacyAdsr = false;
    this.hasTimedStages = this.stages.some((segment) => this.samples(segment.durationSeconds) > 0);
  }

  setParameter(id: string, value: number): void {
    if (id === 'attack-seconds') {
      assertRange(id, value, 0, 60);
      this.attackSeconds = value;
      this.refreshLegacyStages();
      return;
    }
    if (id === 'decay-seconds') {
      assertRange(id, value, 0, 60);
      this.decaySeconds = value;
      this.refreshLegacyStages();
      return;
    }
    if (id === 'sustain-level') {
      assertRange(id, value, 0, 1);
      this.sustainLevel = value;
      this.refreshLegacyStages();
      return;
    }
    if (id === 'release-seconds') {
      assertRange(id, value, 0, 60);
      this.releaseSeconds = value;
      return;
    }
    if (id === 'mode') {
      if (!Number.isInteger(value) || value < 0 || value >= ENVELOPE_MODES.length) {
        throw new RangeError('mode must be 0, 1 or 2');
      }
      this.mode = ENVELOPE_MODES[value]!;
      return;
    }
    throw new RangeError(`unknown parameter: ${id}`);
  }

  processInto(output: Float32Array, gate: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || output.length < frames || gate.length < frames) throw new RangeError('output and gate buffers must cover frames');
    for (let index = 0; index < frames; index += 1) if (!Number.isFinite(gate[index])) throw new RangeError('gate samples must be finite');
    for (let index = 0; index < frames; index += 1) {
      const nextGateHigh = gate[index]! > 0;
      const rising = nextGateHigh && !this.gateHigh;
      const falling = !nextGateHigh && this.gateHigh;
      this.gateHigh = nextGateHigh;
      if (rising) this.trigger();
      if (falling && this.mode !== 'one-shot') this.beginRelease();
      output[index] = this.advance();
    }
  }

  private validateParameters(): void {
    assertRange('attack-seconds', this.attackSeconds, 0, 60);
    assertRange('decay-seconds', this.decaySeconds, 0, 60);
    assertRange('sustain-level', this.sustainLevel, 0, 1);
    assertRange('release-seconds', this.releaseSeconds, 0, 60);
    if (!(ENVELOPE_MODES as readonly string[]).includes(this.mode)) throw new RangeError('mode is invalid');
  }

  private validateStages(stages: readonly EnvelopeSegment[]): readonly ValidatedSegment[] {
    if (stages.length < 1 || stages.length > 128) {
      throw new RangeError('stages must contain between 1 and 128 entries');
    }
    return Object.freeze(stages.map((segment, index) => {
      assertRange(`stages[${index}].durationSeconds`, segment.durationSeconds, 0, 60);
      assertRange(`stages[${index}].targetLevel`, segment.targetLevel, 0, 1);
      return Object.freeze({
        durationSeconds: segment.durationSeconds,
        targetLevel: segment.targetLevel,
        curve: validateCurve(segment.curve),
        legacyStage: 'stage' as const,
      });
    }));
  }

  private createAdsrStages(): readonly ValidatedSegment[] {
    return Object.freeze([
      Object.freeze({
        durationSeconds: this.attackSeconds,
        targetLevel: 1,
        curve: 'linear' as const,
        legacyStage: 'attack' as const,
      }),
      Object.freeze({
        durationSeconds: this.decaySeconds,
        targetLevel: this.sustainLevel,
        curve: 'linear' as const,
        legacyStage: 'decay' as const,
      }),
    ]);
  }

  private refreshLegacyStages(): void {
    if (this.usesLegacyAdsr) {
      // Legacy stage records describe the fixed two-stage shape only. Read
      // current scalar values at stage entry instead of allocating/freeze'ing a
      // new program on every live setter (also safe at a graph apply boundary).
      this.hasTimedStages = this.samples(this.attackSeconds) > 0 || this.samples(this.decaySeconds) > 0;
    }
  }

  private samples(seconds: number): number {
    return Math.round(seconds * this.sampleRate);
  }

  private trigger(): void {
    this.stageIndex = 0;
    this.beginProgramStage();
  }

  /**
   * Enter the next timed stage, consuming zero-duration stages atomically.
   * At most one pass is consumed before completion handling, preventing an
   * all-zero loop program from spinning forever.
   */
  private beginProgramStage(): void {
    while (this.stageIndex < this.stages.length) {
      const segment = this.stages[this.stageIndex]!;
      const duration = this.usesLegacyAdsr ? (this.stageIndex === 0 ? this.attackSeconds : this.decaySeconds) : segment.durationSeconds;
      const target = this.usesLegacyAdsr ? (this.stageIndex === 0 ? 1 : this.sustainLevel) : segment.targetLevel;
      const sampleCount = this.samples(duration);
      if (sampleCount === 0) {
        this.value = target;
        this.stageIndex += 1;
        continue;
      }
      this.stage = segment.legacyStage;
      this.stageStartValue = this.value;
      this.stageTarget = target;
      this.stageCurve = segment.curve;
      this.stageSamples = sampleCount;
      this.stageElapsed = 0;
      return;
    }
    this.finishProgram();
  }

  private finishProgram(): void {
    if (this.mode === 'one-shot') {
      this.beginRelease();
      return;
    }
    if (this.mode === 'loop' && this.gateHigh) {
      if (this.hasTimedStages) {
        this.stageIndex = 0;
        this.beginProgramStage();
      } else {
        this.stage = 'sustain';
      }
      return;
    }
    this.stage = 'sustain';
  }

  private beginRelease(): void {
    if (this.stage === 'idle') return;
    this.stage = 'release';
    this.stageStartValue = this.value;
    this.stageTarget = 0;
    this.stageCurve = 'linear';
    this.stageSamples = this.samples(this.releaseSeconds);
    this.stageElapsed = 0;
    if (this.stageSamples === 0) {
      this.value = 0;
      this.stage = 'idle';
    }
  }

  private advance(): number {
    if (this.stage === 'attack' || this.stage === 'decay' || this.stage === 'stage' || this.stage === 'release') {
      this.stageElapsed += 1;
      const progress = Math.min(1, this.stageElapsed / this.stageSamples);
      const shaped = curveValue(this.stageCurve, progress);
      this.value = this.stageStartValue + (this.stageTarget - this.stageStartValue) * shaped;
      if (this.stageElapsed >= this.stageSamples) {
        this.value = this.stageTarget;
        if (this.stage === 'release') {
          this.stage = 'idle';
        } else {
          this.stageIndex += 1;
          this.beginProgramStage();
        }
      }
    }
    if (!Number.isFinite(this.value)) throw new RangeError('envelope produced a non-finite value');
    this.value = Math.max(0, Math.min(1, this.value));
    return this.value;
  }
}

class EnvelopeModuleInstance implements DspModuleInstance {
  private readonly envelope: EnvelopeGenerator;
  private readonly maxBlockSize: number;

  constructor(sampleRate: number, maxBlockSize: number) {
    this.maxBlockSize = maxBlockSize;
    this.envelope = new EnvelopeGenerator({ sampleRate });
  }

  reset(): void { this.envelope.reset(); }
  setParameter(id: string, value: number): void { this.envelope.setParameter(id, value); }

  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const gate = block.controls.gate;
    const output = block.outputs.out;
    if (!gate || gate.length < block.frames) throw new RangeError('gate control buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.envelope.processInto(output, gate, block.frames);
  }
}

export const mod014 = defineDspModule({
  descriptor: {
    id: 'mir.mod-014',
    name: 'General envelope generator',
    version: '0.1.0',
    inputs: [{ id: 'gate', kind: 'control', channels: 1 }],
    outputs: [{ id: 'out', kind: 'control', channels: 1 }],
    parameters: [
      { id: 'attack-seconds', defaultValue: 0.01, minValue: 0, maxValue: 60, unit: 's' },
      { id: 'decay-seconds', defaultValue: 0.1, minValue: 0, maxValue: 60, unit: 's' },
      { id: 'sustain-level', defaultValue: 0.7, minValue: 0, maxValue: 1, unit: 'ratio' },
      { id: 'release-seconds', defaultValue: 0.2, minValue: 0, maxValue: 60, unit: 's' },
      { id: 'mode', defaultValue: 0, minValue: 0, maxValue: 2, unit: 'enum' },
    ],
  },
  createInstance(context) {
    if (!(context.sampleRate > 0) || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new EnvelopeModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
